// src/FlightDataPage.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Button, Box, Typography, IconButton, Dialog } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import NavBar from './NavBar'; // Correct import path
import './FlightDataPage.css';

// Import the new components
import AccordionTabs from './components/flight-data/AccordionTabs';
import BreakdownArea from './components/flight-data/BreakdownArea';
import DispatchBoard from './components/flight-data/DispatchBoard';
import { useBreakdownView } from './contexts/BreakdownViewContext';

function FlightDataPage() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // State for flight data and parameters
  const [tdrData, setTdrData] = useState(null);
  const [brockData, setBrockData] = useState(null);
  const [freightManifestData, setFreightManifestData] = useState(null);
  const [params, setParams] = useState({
    isPriorityFlight: false,
    hasFreightManifest: false,
    hotThreshold: 30,
    coldThresholdHours: 2
  });
  const [inboundETA, setInboundETA] = useState('');

  // Flights the TDR paper mentions but the parser couldn't produce — a TDR
  // miss means the flight is absent from the board entirely, so it gets a
  // banner the dispatcher can't miss. Dismissal is per page load on purpose:
  // a missing flight should re-confess if the page is revisited.
  const [parseWarnings, setParseWarnings] = useState([]);
  // Bagged flights absent from every Brock bin — only populated when the
  // paste shows the truncation signature (see the backend check), so a
  // non-empty list means the Brock itself is suspect, not one feed miss.
  const [brockWarnings, setBrockWarnings] = useState([]);
  // The mirror: scanned bags with no TDR flight. The full list feeds the
  // board's ghost tiles; the mass flag opens the warning window because
  // the TDR itself may be cut short.
  const [tdrMissingFlights, setTdrMissingFlights] = useState([]);
  const [tdrMissingMass, setTdrMissingMass] = useState(false);

  // The hard stop on arrival — the modal is the whole warning.
  const [parseModalOpen, setParseModalOpen] = useState(false);

  // Note for all drivers - initialize from localStorage
  const [flightNote, setFlightNote] = useState(() => {
    return localStorage.getItem('flightNoteForAllDrivers') || '';
  });

  // Save flight note to localStorage when it changes. A throw here (storage
  // full, private mode) must not crash the page — the note stays in state.
  useEffect(() => {
    try { localStorage.setItem('flightNoteForAllDrivers', flightNote); } catch {}
  }, [flightNote]);

  // View mode + details drawer live in a shared context so the NavBar
  // controls (Classic/Focus toggle and DETAILS button, shown only on this
  // page) and this page stay in sync. 'classic' = map/tabs/legend stacked
  // above the board; 'focus' = board-first with that content in a pull-down
  // drawer opened from the NavBar.
  const { viewMode, detailsOpen, setDetailsOpen } = useBreakdownView();

  // Leaving focus mode closes the drawer so it can't stay open behind classic.
  useEffect(() => {
    if (viewMode === 'classic') setDetailsOpen(false);
  }, [viewMode, setDetailsOpen]);


  // Load data from location state or localStorage
  useEffect(() => {
    if (location.state?.tdrData && location.state?.brockData) {
      // Data passed via navigation
      setTdrData(location.state.tdrData);
      setBrockData(location.state.brockData);
      
      // Load freight manifest data if available
      if (location.state.freightManifestData) {
        setFreightManifestData(location.state.freightManifestData);
      }
      
      // Load parameters if available
      if (location.state.breakdownParams) {
        setParams(location.state.breakdownParams);
        // Flight note is managed via localStorage (shift-level setting),
        // so we don't restore it from per-flight breakdownParams.
      }

      setParseWarnings(location.state.parseWarnings || []);
      setBrockWarnings(location.state.brockWarnings || []);
      setTdrMissingFlights(location.state.tdrMissingFlights || []);
      setTdrMissingMass(location.state.tdrMissingMass || false);
      setParseModalOpen(
        (location.state.parseWarnings || []).length > 0 ||
        (location.state.brockWarnings || []).length > 0 ||
        (location.state.tdrMissingFlights || []).length > 0
      );

      // Extract and store the inbound ETA for reference
      if (location.state.tdrData?.inbound?.eta) {
        setInboundETA(location.state.tdrData.inbound.eta);
      }
    } else {
      // Try to load from localStorage
      let storedFlights = [];
      try {
        storedFlights = JSON.parse(localStorage.getItem('flightList') || '[]');
        if (!Array.isArray(storedFlights)) storedFlights = [];
      } catch (e) {
        console.warn('Corrupt flightList in localStorage; resetting.', e);
        storedFlights = [];
      }
      if (storedFlights.length > 0) {
        const latestFlight = storedFlights[storedFlights.length - 1];
        setTdrData(latestFlight.tdrData);
        setBrockData(latestFlight.brockData);
        
        // Load freight manifest if available
        if (latestFlight.freightManifestData) {
          setFreightManifestData(latestFlight.freightManifestData);
        }
        
        // Load parameters if available
        if (latestFlight.breakdownParams) {
          setParams(latestFlight.breakdownParams);
          // Flight note is managed via localStorage (shift-level setting),
          // so we don't restore it from per-flight breakdownParams.
        }

        setParseWarnings(latestFlight.parseWarnings || []);
        setBrockWarnings(latestFlight.brockWarnings || []);
        setTdrMissingFlights(latestFlight.tdrMissingFlights || []);
        setTdrMissingMass(latestFlight.tdrMissingMass || false);
        setParseModalOpen(
          (latestFlight.parseWarnings || []).length > 0 ||
          (latestFlight.brockWarnings || []).length > 0 ||
          (latestFlight.tdrMissingFlights || []).length > 0
        );

        // Extract and store the inbound ETA
        if (latestFlight.tdrData?.inbound?.eta) {
          setInboundETA(latestFlight.tdrData.inbound.eta);
        }
      } else {
        // No data available, redirect to home
        navigate('/');
      }
    }
  }, [location.state, navigate]);

  // When parameters change
  useEffect(() => {
    console.log("Parameters updated:", params);
  }, [params]);

  // Navigate to home
  const handleHomeClick = () => {
    navigate('/');
  };

  // Check if we have data to display
  if (!tdrData || !brockData) {
    return (
      <Container>
        <h1>Loading flight data...</h1>
        <Button 
          variant="contained" 
          startIcon={<HomeIcon />}
          onClick={handleHomeClick}
          sx={{ mt: 2 }}
        >
          Return Home
        </Button>
      </Container>
    );
  }

  // Map + tabs + breakdown legend/stats. Same components whether shown inline
  // (classic) or inside the pull-down drawer (focus).
  const detailsContent = (
    <Container>
      {/* Accordion Tabs with frosted glass wrapper */}
      <div className="component-wrapper">
        <AccordionTabs
          tdrData={tdrData}
          brockData={brockData}
          freightManifestData={freightManifestData}
          params={params}
          inboundETA={inboundETA}
        />
      </div>

      {/* Breakdown Area with frosted glass wrapper */}
      <div className="component-wrapper">
        <BreakdownArea
          tdrData={tdrData}
          brockData={brockData}
          inboundETA={inboundETA}
          params={params}
        />
      </div>
    </Container>
  );

  // Details render inline (normal document flow) in classic mode, or in focus
  // mode when the folder tab is toggled open. Opening just pushes the board
  // down like classic — no overlay, scroll as normal.
  const showDetailsInline = viewMode === 'classic' || (viewMode === 'focus' && detailsOpen);

  return (
    <div
      className="flight-data-container"
      style={{ paddingTop: showDetailsInline ? undefined : 0 }}
    >
      {/* Classic: details stacked inline above the board. Rendered as null (not
          omitted) in focus mode so the DispatchBoard below keeps its DOM slot
          and stays mounted — preserving in-progress assignments when the
          dispatcher flips views. */}
      {showDetailsInline ? detailsContent : null}


      {/* Dispatch board — single instance, stable position across both views. */}
      <Box key="dispatch-board" sx={{ width: '100%', mt: showDetailsInline ? 2 : 0 }}>
        <DispatchBoard
          tdrData={tdrData}
          brockData={brockData}
          freightManifestData={freightManifestData}
          params={params}
          inboundETA={inboundETA}
          inboundGate={tdrData?.inbound?.arrivalGate}
          flightNote={flightNote}
          setFlightNote={setFlightNote}
          tdrMissingFlights={tdrMissingFlights}
        />
      </Box>

      {/* Parse-miss modal — the hard stop on arrival, same visual language
          as the solver's "Manual review needed" dialog. */}
      <Dialog
        open={parseModalOpen}
        onClose={() => setParseModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(31, 41, 55, 0.97)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(249, 182, 18, 0.4)',
            borderRadius: 2,
            color: '#fff',
          },
        }}
      >
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          px: 3, py: 1.5,
        }}>
          <WarningAmberIcon sx={{ color: '#F9B612' }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#F9B612', fontWeight: 700, fontSize: '1.05rem' }}>
              {parseWarnings.length > 0 ? 'Missing from the board'
                : brockWarnings.length > 0 ? 'Load Not Confirmed'
                : 'Not on the TDR'}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem', mt: 0.25 }}>
              {parseWarnings.length > 0
                ? `${parseWarnings.length} flight${parseWarnings.length === 1 ? '' : 's'} on the TDR paper couldn't be read in.`
                : brockWarnings.length > 0
                ? 'These bags show on the TDR but not in the Brock.'
                : 'These bags are scanned in the Brock but the TDR has no flight for them.'}
            </Typography>
          </Box>
          <IconButton onClick={() => setParseModalOpen(false)} size="small" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ px: 3, py: 2.5 }}>
          {parseWarnings.length > 0 && (
            <>
              <Box sx={{
                backgroundColor: 'rgba(0,0,0,0.25)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderLeft: '3px solid #F9B612',
                borderRadius: 1.5,
                overflow: 'hidden',
              }}>
                {parseWarnings.map((w, idx) => (
                  <Typography key={w} sx={{
                    color: '#fff', fontSize: '0.88rem', px: 1.5, py: 1, lineHeight: 1.45,
                    borderBottom: idx === parseWarnings.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.05)',
                  }}>
                    {w}
                  </Typography>
                ))}
              </Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.8rem', mt: 1.5 }}>
                To fix: add it with <Box component="span" sx={{ color: '#F9B612', fontWeight: 700 }}>CUSTOM FLIGHT TILE</Box> on
                the board — or correct the TDR line and re-paste.
              </Typography>
            </>
          )}
          {/* Brock half of the net: bagged flights the Brock never confirmed.
              Only fires on the truncated-paste signature, and the language
              stops at "not confirmed" — it never guesses bins. */}
          {brockWarnings.length > 0 && (
            <>
              {parseWarnings.length > 0 && (
                <Typography sx={{ color: '#F9B612', fontWeight: 700, fontSize: '0.9rem', mt: 2.5, mb: 1 }}>
                  Load Not Confirmed
                </Typography>
              )}
              <Box sx={{
                backgroundColor: 'rgba(0,0,0,0.25)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderLeft: '3px solid #F9B612',
                borderRadius: 1.5,
                overflow: 'hidden',
                mt: parseWarnings.length > 0 ? 0 : 0,
              }}>
                {brockWarnings.map((w, idx) => (
                  <Typography key={w.flightNumber} sx={{
                    color: '#fff', fontSize: '0.88rem', px: 1.5, py: 1, lineHeight: 1.45,
                    borderBottom: idx === brockWarnings.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.05)',
                  }}>
                    {w.flightNumber} to {w.destination} — {w.bags} bag{w.bags === 1 ? '' : 's'} on
                    the TDR, but the Brock shows no bin data.
                  </Typography>
                ))}
              </Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.8rem', mt: 1.5 }}>
                Exercise caution with these connections — the bags may not be on the
                aircraft. Re-pasting the
                full <Box component="span" sx={{ color: '#F9B612', fontWeight: 700 }}>Brock</Box> will
                rule out a short paste.
              </Typography>
            </>
          )}
          {/* The mirror: scanned bags with no TDR flight. Any ghost gets
              announced — a lone dashed tile at the bottom of a long
              Unassigned column is easy to miss. The re-paste advisory
              below only shows on the mass signature. */}
          {tdrMissingFlights.length > 0 && (
            <>
              {(parseWarnings.length > 0 || brockWarnings.length > 0) && (
                <Typography sx={{ color: '#F9B612', fontWeight: 700, fontSize: '0.9rem', mt: 2.5, mb: 1 }}>
                  Not on the TDR
                </Typography>
              )}
              <Box sx={{
                backgroundColor: 'rgba(0,0,0,0.25)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderLeft: '3px solid #F9B612',
                borderRadius: 1.5,
                overflow: 'hidden',
              }}>
                {tdrMissingFlights.map((f, idx, arr) => (
                  <Typography key={f.flightNumber} sx={{
                    color: '#fff', fontSize: '0.88rem', px: 1.5, py: 1, lineHeight: 1.45,
                    borderBottom: idx === arr.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.05)',
                  }}>
                    {f.flightNumber} to {f.destination} — {f.bags} bag{f.bags === 1 ? '' : 's'} scanned
                    in the Brock, no TDR row.
                  </Typography>
                ))}
              </Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.8rem', mt: 1.5 }}>
                They're on the board as dashed tiles — confirm the ones that are real.
                {tdrMissingMass && (
                  <> Re-pasting the full <Box component="span" sx={{ color: '#F9B612', fontWeight: 700 }}>TDR</Box> will
                  rule out a short paste.</>
                )}
              </Typography>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 3, pb: 2, pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Button
            onClick={() => setParseModalOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: '#F9B612', color: '#000',
              fontWeight: 700, textTransform: 'none', px: 3,
              '&:hover': { backgroundColor: '#e0a410' },
            }}
          >
            Got it
          </Button>
        </Box>
      </Dialog>

      {/* The focus-mode DETAILS toggle lives in the NavBar (next to the
          Classic/Focus toggle) — see NavBar.js. */}
    </div>
  );
}

export default FlightDataPage;